import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/Spinner';
import { KC } from '../../lib/kyckConnect/links';
import { SETUP_STEP_QUERY, setupStepFromQuery, type SetupStatus, type SetupStepId } from '../../lib/kyckConnect/setupStatus';
import type { KyckConnectSetupResponse } from '../../types/kyckConnect';
// ⚠️ FILE-LEVEL IMPORTS, not the module barrels: the account page hosts this through KyckConnectTab, and the leadBot / leadRelay /
// speedToLead barrels reach the accounts barrel, which would close a barrel-to-barrel cycle (a failure at module init, not at compile).
import { BusinessProfileEditors } from '../businessProfile/BusinessProfileEditors';
import { BotSetup } from '../leadBot/BotSetup';
import { LeadRelaySummaryTile } from '../leadRelay/LeadRelaySummaryTile';
import { ClubSettingsForm } from '../speedToLead/ClubSettingsForm';
import { DirectTestLeadPanel } from '../speedToLead/DirectTestLeadPanel';
import { fetchSetup } from './api';

// THE SETUP CHECKLIST (redesign Phase 1): six steps, each with a status the SERVER computes (done / incomplete / off), each opening
// its editor IN PLACE. The open step is the URL's ?step=, so a "Fix" link from the Overview lands on the right editor.
//
// ⚠️ The statuses come from ONE server function (`computeSetup`), shared with the Overview. This component decides nothing about
// whether a club is set up; it shows what it is told. The editors are the existing components, unchanged.

const STATUS: Record<SetupStatus, { label: string; variant: BadgeVariant }> = {
  done: { label: 'Done', variant: 'success' },
  incomplete: { label: 'Incomplete', variant: 'warning' },
  off: { label: 'Off', variant: 'neutral' },
};

function Body({ id, setup, accountId, isAdmin }: { id: SetupStepId; setup: KyckConnectSetupResponse; accountId: string; isAdmin: boolean }) {
  switch (id) {
    case 'services':
      return (
        <ClubSettingsForm
          accountId={accountId}
          isAdmin={isAdmin}
          section="services"
          relay={
            <div className="mt-1 space-y-1 text-sm text-slate-700" data-testid="relay-card">
              <p>{setup.routes.total > 0 ? <>On: <strong>{setup.routes.total} route{setup.routes.total === 1 ? '' : 's'}</strong> ({setup.routes.activeLive} live).</> : 'Off: no lead route points at this club.'}</p>
              {isAdmin ? <Link className="text-sm font-medium underline" to={KC.relayRoutes}>Open Lead relay routes</Link> : <p className="text-xs text-slate-500">Only a Kyck admin can edit lead routes.</p>}
            </div>
          }
        />
      );
    case 'profile':
      return <BusinessProfileEditors accountId={accountId} />;
    case 'relay':
      return (
        <div className="space-y-3">
          <LeadRelaySummaryTile accountId={accountId} />
          {!setup.services.leadRelay && <p className="text-sm text-slate-600">No lead route points at this club yet.</p>}
          {isAdmin ? (
            <Link className="text-sm font-medium underline" to={KC.relayRoutes}>Edit routes in Lead relay</Link>
          ) : (
            <p className="text-xs text-slate-500">Only a Kyck admin can edit lead routes.</p>
          )}
        </div>
      );
    case 'aiSms':
      return <BotSetup accountId={accountId} accountName={setup.accountName} accountIndustry={setup.accountIndustry} isAdmin={isAdmin} />;
    case 'speedToLead':
      return <ClubSettingsForm accountId={accountId} isAdmin={isAdmin} section="calls" />;
    case 'numberPool':
      return (
        <div className="space-y-2 text-sm text-slate-700" data-testid="step-number-pool">
          <p>{setup.pool ? <>Pool: <strong>{setup.pool.name}</strong>{setup.pool.numbers.length > 0 ? `, numbers ${setup.pool.numbers.join(', ')}` : ''}.</> : 'This club is in no number pool.'}</p>
          <p>{setup.botNumber ? <>The bot texts from <strong>{setup.botNumber}</strong>.</> : 'The bot has no number of its own, so it texts from the shared Kyck number.'}</p>
          {setup.steps.find((s) => s.id === 'numberPool')?.status === 'incomplete' && (
            <p role="alert" data-testid="pool-warning" className="rounded bg-amber-50 p-2 text-xs text-amber-800">
              {setup.steps.find((s) => s.id === 'numberPool')?.detail}
            </p>
          )}
          <Link className="text-sm font-medium underline" to={KC.numbers}>Open Numbers and pools</Link>
          <ClubSettingsForm accountId={accountId} isAdmin={isAdmin} section="callbacks" />
        </div>
      );
    case 'goLive':
      return (
        <div className="space-y-4">
          <ul className="space-y-1 text-sm" data-testid="go-live-checks">
            {setup.goLive.map((c) => (
              <li key={c.id} className="flex items-start gap-2">
                <Badge variant={c.ok ? 'success' : 'warning'}>{c.ok ? 'Pass' : 'Not met'}</Badge>
                <span><span className="font-medium text-slate-800">{c.label}.</span> <span className="text-slate-600">{c.detail}</span></span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">These are read-only checks of what must be true before this club takes real leads. Change each in its own step.</p>
          {isAdmin ? <DirectTestLeadPanel clubId={accountId} /> : <p className="text-xs text-slate-500">Only a Kyck admin can send a test lead.</p>}
        </div>
      );
  }
}

export function SetupChecklist({ accountId, isAdmin }: { accountId: string; isAdmin: boolean }) {
  const [params, setParams] = useSearchParams();
  const open = setupStepFromQuery(params.get('step'));
  const query = useQuery({ queryKey: ['kyckconnect', 'setup', accountId], queryFn: () => fetchSetup(accountId) });

  const toggle = (id: SetupStepId) => {
    const next = new URLSearchParams(params);
    if (open === id) next.delete('step');
    else next.set('step', SETUP_STEP_QUERY[id]);
    setParams(next, { replace: true });
  };

  if (query.isLoading) return <Spinner />;
  if (query.error || !query.data) return <p className="text-sm text-red-600">{query.error instanceof Error ? query.error.message : 'Could not load the setup checklist.'}</p>;
  const setup = query.data;

  return (
    <ol className="space-y-3" data-testid="setup-checklist">
      {setup.steps.map((step, i) => {
        const isOpen = open === step.id && step.visible;
        // A step whose service is off is ONE quiet line, never a half-hidden editor with settings that do nothing.
        if (!step.visible) {
          return (
            <li key={step.id} className="rounded-md border border-dashed border-slate-200 px-4 py-3" data-testid={`step-${step.id}`} data-hidden="true">
              <span className="text-sm text-slate-500">{i + 1}. {step.label}: {step.hiddenMessage}</span>
            </li>
          );
        }
        return (
          <li key={step.id} className="rounded-md border border-slate-200 bg-white" data-testid={`step-${step.id}`}>
            <button type="button" className="flex w-full items-center justify-between gap-3 p-4 text-left" aria-expanded={isOpen} onClick={() => toggle(step.id)}>
              <span>
                <span className="text-sm font-semibold text-slate-900">{i + 1}. {step.label}</span>
                <span className="block text-xs text-slate-500">{step.detail}</span>
              </span>
              <Badge variant={STATUS[step.status].variant}>{STATUS[step.status].label}</Badge>
            </button>
            {isOpen && <div className="border-t border-slate-200 p-4"><Body id={step.id} setup={setup} accountId={accountId} isAdmin={isAdmin} /></div>}
          </li>
        );
      })}
    </ol>
  );
}
